import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Plus, Star, Trash2, Save, X, ChevronLeft } from "lucide-react";

const PROTEINS = ["Beef", "Eggs", "Fish", "Chicken"];
const OCCASIONS = ["Breakfast", "Brunch", "Lunch", "Dinner", "Snack"];
const CARBS = ["Lower", "Moderate", "Higher"];
const EMPTY = {
  name: "", primary_protein: "Chicken", meal_occasion: "Dinner",
  ingredients: [""], build_it: "", meal_profile: "",
  why_it_works: "", family_swap: "", image: "",
  tags: [], carb_profile: "Moderate", featured: false,
};

export default function AdminMeals() {
  const [meals, setMeals] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/meals");
      setMeals(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return meals;
    return meals.filter((m) => m.name.toLowerCase().includes(s) || m.primary_protein.toLowerCase().includes(s));
  }, [meals, q]);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (m) => {
    setEditing(m.id);
    setForm({
      name: m.name, primary_protein: m.primary_protein, meal_occasion: m.meal_occasion,
      ingredients: m.ingredients?.length ? m.ingredients : [""],
      build_it: m.build_it || "", meal_profile: m.meal_profile || "",
      why_it_works: m.why_it_works || "", family_swap: m.family_swap || "",
      image: m.image || "", tags: m.tags || [],
      carb_profile: m.carb_profile || "Moderate", featured: !!m.featured,
    });
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Meal name required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, ingredients: form.ingredients.filter((i) => i.trim()) };
      if (editing === "new") await api.post("/admin/meals", payload);
      else await api.patch(`/admin/meals/${editing}`, payload);
      toast.success("Saved");
      close(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this meal? Members will no longer see it.")) return;
    try { await api.delete(`/admin/meals/${id}`); toast.success("Archived"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleFeatured = async (m) => {
    try { await api.patch(`/admin/meals/${m.id}`, { featured: !m.featured }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setIng = (idx, v) => setForm((f) => ({ ...f, ingredients: f.ingredients.map((it, i) => i === idx ? v : it) }));
  const addIng = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, ""] }));
  const removeIng = (idx) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, i) => i !== idx) }));

  return (
    <div className="space-y-6" data-testid="admin-meals-page">
      <div>
        <Link to="/app/admin" className="text-xs uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1 hover:text-primary">
          <ChevronLeft size={14} /> Admin
        </Link>
        <div className="flex items-end justify-between flex-wrap gap-3 mt-3">
          <div>
            <div className="label-eyebrow">Meals CMS</div>
            <h1 className="wordmark mt-2 text-4xl md:text-5xl uppercase">Meal Library.</h1>
            <p className="mt-2 text-sm text-muted-foreground">{meals.length} meals total · {meals.filter((m) => m.featured).length} featured · {meals.filter((m) => m.archived).length} archived</p>
          </div>
          <button onClick={openNew} className="btn-primary btn-sm" data-testid="admin-meal-new"><Plus size={14} /> New meal</button>
        </div>
      </div>

      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search meals…"
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="admin-meal-search" />

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="py-2 px-4">Name</th>
                <th className="py-2 px-4">Protein</th>
                <th className="py-2 px-4">Occasion</th>
                <th className="py-2 px-4">Carbs</th>
                <th className="py-2 px-4">Feat.</th>
                <th className="py-2 px-4">Status</th>
                <th className="py-2 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} className="border-b border-border/60" data-testid={`admin-meal-row-${m.slug}`}>
                  <td className="py-2 px-4 font-head uppercase text-sm">{m.name}</td>
                  <td className="py-2 px-4">{m.primary_protein}</td>
                  <td className="py-2 px-4">{m.meal_occasion}</td>
                  <td className="py-2 px-4">{m.carb_profile}</td>
                  <td className="py-2 px-4">
                    <button onClick={() => toggleFeatured(m)} className={m.featured ? "text-primary" : "text-muted-foreground hover:text-primary"}>
                      <Star size={16} fill={m.featured ? "currentColor" : "none"} />
                    </button>
                  </td>
                  <td className="py-2 px-4 text-xs">{m.archived ? <span className="text-red-500">Archived</span> : "Live"}</td>
                  <td className="py-2 px-4 text-right">
                    <button onClick={() => openEdit(m)} className="text-xs uppercase tracking-widest text-primary mr-3 hover:underline">Edit</button>
                    {!m.archived && <button onClick={() => archive(m.id)} className="text-xs uppercase tracking-widest text-red-500 hover:underline">Archive</button>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="py-6 px-4 text-center text-sm text-muted-foreground">No meals.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-foreground/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={close}>
          <div className="w-full max-w-2xl bg-background rounded-lg border border-border my-8" onClick={(e) => e.stopPropagation()} data-testid="admin-meal-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="font-head uppercase">{editing === "new" ? "New Meal" : "Edit Meal"}</div>
              <button onClick={close} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="admin-meal-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Primary Protein</label>
                  <select value={form.primary_protein} onChange={(e) => setForm({ ...form, primary_protein: e.target.value })}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    {PROTEINS.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Occasion</label>
                  <select value={form.meal_occasion} onChange={(e) => setForm({ ...form, meal_occasion: e.target.value })}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    {OCCASIONS.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Carb Profile</label>
                  <select value={form.carb_profile} onChange={(e) => setForm({ ...form, carb_profile: e.target.value })}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                    {CARBS.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Featured</label>
                  <label className="mt-1 flex items-center gap-2 text-sm h-9">
                    <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Show on Eat Home
                  </label>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Image URL</label>
                <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="https://…" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Meal Profile</label>
                <input value={form.meal_profile} onChange={(e) => setForm({ ...form, meal_profile: e.target.value })}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="HIGH PROTEIN · MODERATE CARBS" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Ingredients</label>
                <div className="mt-1 space-y-2">
                  {form.ingredients.map((it, i) => (
                    <div key={i} className="flex gap-2">
                      <input value={it} onChange={(e) => setIng(i, e.target.value)}
                        className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm" />
                      <button onClick={() => removeIng(i)} className="text-muted-foreground hover:text-red-500"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <button onClick={addIng} className="text-xs uppercase tracking-widest text-primary hover:underline">+ Add ingredient</button>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Build It</label>
                <textarea value={form.build_it} onChange={(e) => setForm({ ...form, build_it: e.target.value })}
                  rows={3} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Why It Works</label>
                <textarea value={form.why_it_works} onChange={(e) => setForm({ ...form, why_it_works: e.target.value })}
                  rows={2} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Family / Easy Swap</label>
                <textarea value={form.family_swap} onChange={(e) => setForm({ ...form, family_swap: e.target.value })}
                  rows={2} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Tags (comma separated)</label>
                <input value={form.tags.join(", ")} onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Family-friendly, Quick, Batch cook" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end gap-2">
              <button onClick={close} className="btn-secondary btn-sm">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary btn-sm disabled:opacity-40" data-testid="admin-meal-save"><Save size={14} /> {saving ? "Saving…" : "Save meal"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
